import { useCallback, useMemo } from 'react';
import Button, { isButtonEnabled } from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper, { PopUpType } from 'src/components/PopUpContentWrapper';
import useConfirm from 'src/hooks/useConfirm';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from './InvoiceDetailsContext';

const DeleteLineItem = () => {
  const { confirm } = useConfirm();
  const selectedLineItemIds = useContextSelector(
    InvoiceContext,
    (value) => value.selectedLineItemIds
  );
  const invoiceLineItems = useContextSelector(InvoiceContext, (value) => value.invoiceLineItems);
  const setSelectedLineItemIds = useContextSelector(
    InvoiceContext,
    (value) => value.setSelectedLineItemIds
  );
  const setInvoiceLineItems = useContextSelector(
    InvoiceContext,
    (value) => value.setInvoiceLineItems
  );

  const deleteLineItem = useCallback(() => {
    setInvoiceLineItems((lineItems) =>
      lineItems?.filter((lineItem) => !selectedLineItemIds.includes(lineItem.receiptLineItemId))
    );
    setSelectedLineItemIds([]);
  }, [setInvoiceLineItems, selectedLineItemIds, setSelectedLineItemIds]);

  const handleClick = useCallback(() => {
    confirm({
      title: 'Delete Confirmation',
      content: (
        <p>
          Information: To permanently delete the line item(s), you must save the invoice. Until
          then, you can refresh the invoice to restore the deleted line item(s).
        </p>
      ),
      confirmButtonText: 'OK',
      hasNoCancelButton: true,
      width: '520px',
    }).then((isDeleteAccepted) => {
      if (isDeleteAccepted) {
        deleteLineItem();
      }
    });
  }, [confirm, deleteLineItem]);

  const cannotDeleteLineItems = useMemo(() => {
    const selectedLineItems = invoiceLineItems.filter((lineItem) =>
      selectedLineItemIds.includes(lineItem.receiptLineItemId)
    );

    return selectedLineItems.filter((lineItem) => !isButtonEnabled(lineItem.canDelete));
  }, [selectedLineItemIds, invoiceLineItems]);

  const { isDeleteButtonDisabled, tooltipMessage, actionType } = useMemo(() => {
    const isDeleteButtonDisabled =
      selectedLineItemIds.length === 0 || cannotDeleteLineItems.length > 0;
    const tooltipMessage: string[] = [];
    let actionType: PopUpType = 'Information';
    if (selectedLineItemIds.length === 0) {
      tooltipMessage.push('Please select Line Item(s) to Delete.');
      actionType = 'Information';
    } else if (cannotDeleteLineItems.length > 0) {
      cannotDeleteLineItems.forEach((lineItem) => {
        tooltipMessage.push(lineItem.productItemNum || 'null');
      });
      actionType = 'Error';
    }

    return {
      isDeleteButtonDisabled,
      tooltipMessage,
      actionType,
    };
  }, [selectedLineItemIds, cannotDeleteLineItems]);

  return (
    <PopUp
      show={isDeleteButtonDisabled}
      content={
        <div className="m-1 max-w-[350px] max-h-[250px] overflow-auto">
          <PopUpContentWrapper
            title={cannotDeleteLineItems.length > 0 ? 'Line Item(s) below cannot be deleted' : ''}
            type={actionType}
            contents={tooltipMessage}
          />
        </div>
      }
    >
      <Button color="error" onClick={handleClick} disabled={isDeleteButtonDisabled}>
        Delete
      </Button>
    </PopUp>
  );
};

export default DeleteLineItem;
